// joystick3d — the on-canvas "3d text direction" trackball (owner
// 2026-07-16: "if there is a 3d object on the canvas, we need a 'joystick'
// in the canvas to adjust the 3d text direction").
//
// WHAT IT IS: one fabric CUSTOM CONTROL (per-INSTANCE `controls` entry — the
// approach chosen over a DOM overlay because fabric computes control
// positions from the object's own transform chain, so zoom/pan/rotate all
// come for free and exports never see it: controls are interaction-canvas
// artifacts, toObject()/toDataURL() know nothing of them). A small dark
// circular pad (~28px, house style) floats just outside the selection
// bbox's bottom-right corner; dragging it is a trackball — dx turns the
// text (ry), dy tilts it (rx), Shift = fine ×0.25, double-click resets to
// the default hero tilt. It appears only for 3D objects:
//   - fx-extrude Textboxes (data.fx holds an active 'extrude' step) — drags
//     patch the step's rx/ry through the engine's patchTextFx and drive
//     throttled low-res re-bakes via fxRequestUpgrade for live feedback
//     (time-budgeted from the measured bake cost — SwiftShader throttles
//     itself down, a real GPU runs ~5 bakes/s), with the seam's
//     __fxDragTilt cheap-transform preview covering the gaps between
//     bakes. Release = final full-quality bake + ONE history entry
//     (commit; the drag itself never fires object:modified — the action
//     handler self-manages rendering and returns false).
//   - baked text3d IMAGES (data.tool 'text3d') — drags show a cheap
//     scale/skew preview on the image itself (restored on release: a
//     re-bake per drag tick would cost a history entry each — the modal's
//     batch contract), release restores the transform and drives ONE
//     editor.regenerateText3d(obj, { rotation: { rx, ry } }) = ONE entry.
//
// ATTACH LIFECYCLE: ensureJoystick3d(obj) is idempotent and self-deciding
// (attaches when isJoystick3dTarget, detaches when not). It runs from
//   - the selection handlers (single selection — controls only render on
//     the active object anyway),
//   - applyFxSteps (extrude added/removed while selected — slider to 0
//     must drop the pad in the same gesture),
//   - reattachFxDeep (load/undo/redo/clone rebuild instances; the walk that
//     re-installs the fx seam re-installs the pad).
// The controls bag is runtime-only: `obj.controls = { ...defaults, pad }`
// never serializes (fabric's toObject has no controls key — probe-verified).
//
// DOUBLE-CLICK: fabric would route a dblclick on the pad into the object's
// own dblclick surface (text enters editing; text3d opens the edit modal) —
// a WINDOW-level capture listener swallows the event first when it lands on
// the pad and performs the reset instead. Registered once per live canvas
// via setJoystick3dContext, removed by teardownJoystick3d.
//
// This module owns no fabric internals: everything rides the public Control
// API + the engine facade handed in via setJoystick3dContext.

import { Control } from 'fabric';
import { FX_EXTRUDE_DEFAULTS } from './fxExtrude';
import { fxRequestUpgrade } from './fxRender';

const KEY = 'joystick3d';
const ACTION = 'joystick3dRotate';
const PAD_R = 14; // 28px pad
const PAD_OFFSET = 26; // px outside the bbox corner (screen space)
const DEG_PER_PX = 0.35; // Text3DPanel's orbit-drag calibration — same feel
const FINE = 0.25; // Shift multiplier
// TEXT3D_RANGES mirrored by hand: text3dRecipe imports three.js, which must
// never ride the editor boot chunk this module lives on. fxExtrude's
// sanitize clamps to the same numbers.
const RX_MIN = -35;
const RX_MAX = 35;
const RY_MIN = -45;
const RY_MAX = 45;
// Both pipelines share the same hero default (FX_EXTRUDE_DEFAULTS mirrors
// TEXT3D_DEFAULTS.rotation).
const DEFAULT_TILT = { rx: FX_EXTRUDE_DEFAULTS.rx, ry: FX_EXTRUDE_DEFAULTS.ry };

const clamp = (v, min, max) => Math.min(max, Math.max(min, Number(v) || 0));
const round1 = (v) => Math.round(v * 10) / 10;

// canvas → { patchTextFx, regenerateText3d, commit } (the engine facade
// slices the joystick is allowed to drive). WeakMap so a disposed canvas
// never pins its editor; the Set mirrors live canvases for the dblclick
// guard (WeakMaps don't iterate).
const CTX = new WeakMap();
const CANVASES = new Set();

/** True for objects the pad drives: an ACTIVE fx-extrude step or a baked
 * text3d recipe image. */
export function isJoystick3dTarget(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (obj.data?.tool === 'text3d' && obj.data.params) return true;
  const steps = obj.data?.fx;
  return Array.isArray(steps)
    && steps.some((s) => s?.id === 'extrude' && (Number(s.params?.depth) || 0) > 0);
}

function kindOf(obj) {
  return obj.data?.tool === 'text3d' ? 'text3d' : 'fx';
}

/** Current tilt (degrees) from the object's own recipe. */
function tiltOf(obj) {
  if (kindOf(obj) === 'text3d') {
    const r = obj.data.params?.rotation || {};
    return {
      rx: clamp(r.rx ?? DEFAULT_TILT.rx, RX_MIN, RX_MAX),
      ry: clamp(r.ry ?? DEFAULT_TILT.ry, RY_MIN, RY_MAX),
    };
  }
  const step = (obj.data?.fx || []).find((s) => s?.id === 'extrude');
  return {
    rx: clamp(step?.params?.rx ?? DEFAULT_TILT.rx, RX_MIN, RX_MAX),
    ry: clamp(step?.params?.ry ?? DEFAULT_TILT.ry, RY_MIN, RY_MAX),
  };
}

// ---------------------------------------------------------------- drag state
// One pointer, one drag — module singleton (fabric serializes transforms).
let drag = null;

function ctxFor(obj) {
  return (obj && obj.canvas && CTX.get(obj.canvas)) || null;
}

/** Throttled low-res drag bake (fx objects). Budget follows the MEASURED
 * bake wall time (×1.4 headroom, clamped 120–700ms) — a real GPU streams
 * near-live, SwiftShader in probes self-throttles instead of piling up. */
function maybeDragBake(d) {
  if (d.kind !== 'fx' || d.bakeInFlight) return;
  const now = performance.now();
  if (now - d.lastBakeStart < d.budgetMs) return;
  d.lastBakeStart = now;
  d.bakeInFlight = true;
  const req = { rx: d.rx, ry: d.ry };
  const t0 = now;
  fxRequestUpgrade(d.obj, { scaleMul: 0.6 }).then(() => {
    const took = performance.now() - t0;
    d.bakeInFlight = false;
    d.budgetMs = Math.min(700, Math.max(120, took * 1.4));
    if (drag === d) {
      // The stash now shows `req` — re-base the cheap preview delta on it.
      d.baseline = req;
      d.obj.__fxDragTilt = { drx: d.rx - req.rx, dry: d.ry - req.ry };
      d.obj.canvas?.requestRenderAll();
    }
  });
}

function applyDragTilt(d) {
  const obj = d.obj;
  if (d.kind === 'fx') {
    const ctx = ctxFor(obj);
    if (!ctx) return;
    // Param write only — no commit (release lands the ONE history entry).
    ctx.patchTextFx('extrude', { rx: round1(d.rx), ry: round1(d.ry) });
    obj.__fxDragTilt = { drx: d.rx - d.baseline.rx, dry: d.ry - d.baseline.ry };
    maybeDragBake(d);
  } else {
    // Cheap live preview on the image itself (exactly how fabric's own
    // controls give feedback); restored on release, so it never reaches
    // history — the drag fires no object:modified (actionHandler returns
    // false throughout).
    const s0 = d.savedTransform;
    const dyaw = ((d.ry - d.start.ry) * Math.PI) / 180;
    const dpitch = ((d.rx - d.start.rx) * Math.PI) / 180;
    obj.set({
      scaleX: s0.scaleX * Math.max(0.55, Math.cos(dyaw)),
      scaleY: s0.scaleY * Math.max(0.55, Math.cos(dpitch)),
      skewX: clamp(s0.skewX + (d.ry - d.start.ry) * 0.12, -14, 14),
      skewY: clamp(s0.skewY - (d.rx - d.start.rx) * 0.12, -14, 14),
    });
    obj.setCoords();
  }
  obj.canvas?.requestRenderAll();
}

function endDrag(d) {
  const obj = d.obj;
  const ctx = ctxFor(obj);
  if (d.kind === 'fx') {
    delete obj.__fxDragTilt;
    // A plain click (no movement) must cost nothing: no param write, no
    // forced re-bake, no commit — dblclick-reset arrives as two such clicks
    // before the capture handler fires.
    if (ctx && d.moved) {
      ctx.patchTextFx('extrude', { rx: round1(d.rx), ry: round1(d.ry) });
      // Full-quality release bake (replaces the low-res drag stash), then
      // the ONE history entry for the whole gesture.
      fxRequestUpgrade(obj, { scaleMul: 1 });
      ctx.commit();
    }
    obj.canvas?.requestRenderAll();
  } else {
    obj.set(d.savedTransform); // preview off — the bake carries the turn
    obj.setCoords();
    obj.canvas?.requestRenderAll();
    if (ctx && d.moved) {
      // ONE entry: regenerateText3dInPlace runs under the engine's batch.
      ctx.regenerateText3d(obj, { rotation: { rx: round1(d.rx), ry: round1(d.ry) } })
        .catch(() => { /* bake failure leaves the old raster — logged by the engine */ });
    }
  }
}

// --------------------------------------------------------------- the control
/** Pointer position that also works for touch (fabric hands us the raw
 * native event; TouchEvents carry client coords on touches[0]). */
function clientXY(e) {
  const t = e.touches && e.touches[0];
  return { x: e.clientX ?? t?.clientX ?? 0, y: e.clientY ?? t?.clientY ?? 0 };
}

function padMouseDown(eventData, transform) {
  const obj = transform.target;
  if (!isJoystick3dTarget(obj)) return;
  const t = tiltOf(obj);
  drag = {
    obj,
    kind: kindOf(obj),
    start: { ...t },
    rx: t.rx,
    ry: t.ry,
    baseline: { ...t }, // fx: params the current stash was baked at
    last: clientXY(eventData),
    moved: false,
    bakeInFlight: false,
    lastBakeStart: 0,
    budgetMs: 160, // first-tick guess; re-measured per bake
    savedTransform: {
      scaleX: obj.scaleX, scaleY: obj.scaleY, skewX: obj.skewX, skewY: obj.skewY,
    },
  };
}

function padAction(eventData, transform) {
  const d = drag;
  if (!d || d.obj !== transform.target) return false;
  const { x: cx, y: cy } = clientXY(eventData);
  const k = DEG_PER_PX * (eventData.shiftKey ? FINE : 1);
  const dx = cx - d.last.x;
  const dy = cy - d.last.y;
  d.last = { x: cx, y: cy };
  if (!dx && !dy) return false;
  // Trackball: dx turns (ry), dy tilts (rx) — Text3DPanel's orbit signs.
  d.ry = clamp(d.ry + dx * k, RY_MIN, RY_MAX);
  d.rx = clamp(d.rx - dy * k, RX_MIN, RX_MAX);
  d.moved = true;
  applyDragTilt(d);
  // false on purpose: the pad self-manages rendering + history — fabric
  // must not fire object:modified at mouseup (its save would race the
  // text3d preview restore and double the fx commit).
  return false;
}

function padMouseUp() {
  const d = drag;
  drag = null;
  if (d) endDrag(d);
}

function renderPad(ctx, left, top, styleOverride, obj) {
  const active = !!(drag && drag.obj === obj);
  ctx.save();
  ctx.translate(left, top);
  // pad face
  ctx.beginPath();
  ctx.arc(0, 0, PAD_R, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.92)'; // house dark (#0f172a)
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = active ? '#3b82f6' : 'rgba(255, 255, 255, 0.28)';
  ctx.stroke();
  // orbit glyph: sphere outline + equator + meridian + pole dot
  ctx.strokeStyle = active ? '#93c5fd' : '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(0, 0, 6.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(0, 0, 6.5, 2.6, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(0, 0, 2.6, 6.5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, 1.4, 0, Math.PI * 2);
  ctx.fillStyle = active ? '#dbeafe' : '#e2e8f0';
  ctx.fill();
  ctx.restore();
}

function makeControl() {
  return new Control({
    x: 0.5,
    y: 0.5,
    offsetX: PAD_OFFSET,
    offsetY: PAD_OFFSET,
    sizeX: PAD_R * 2,
    sizeY: PAD_R * 2,
    touchSizeX: PAD_R * 2 + 12,
    touchSizeY: PAD_R * 2 + 12,
    cursorStyle: 'grab',
    actionName: ACTION,
    withConnection: false,
    mouseDownHandler: padMouseDown,
    actionHandler: padAction,
    mouseUpHandler: padMouseUp,
    render: renderPad,
  });
}

// ------------------------------------------------------------ attach/detach
/**
 * Idempotently attach/detach the pad to match the object's current recipe.
 * Safe on ANY object (self-deciding), cheap when nothing changes. The
 * per-instance controls bag spreads the class defaults — fabric blesses
 * sharing the default Control instances (they are stateless).
 */
export function ensureJoystick3d(obj) {
  if (!obj || typeof obj !== 'object' || !obj.controls) return;
  const want = isJoystick3dTarget(obj);
  const has = !!obj.controls[KEY];
  if (want && !has) {
    obj.controls = { ...obj.controls, [KEY]: makeControl() };
    obj.setCoords(); // oCoords must include the pad before the next hit-test
  } else if (!want && has) {
    const next = { ...obj.controls };
    delete next[KEY];
    obj.controls = next;
    obj.setCoords();
  }
}

/** Selection-time sync: the pad only ever matters on a single selection
 * (an ActiveSelection wrapper has its own default controls bag). */
export function syncJoystick3dSelection(canvas) {
  const objs = canvas?.getActiveObjects?.() || [];
  if (objs.length === 1) ensureJoystick3d(objs[0]);
}

// -------------------------------------------------- dblclick reset (capture)
function padHit(canvas, e) {
  const obj = canvas.getActiveObject?.();
  if (!obj || !obj.controls?.[KEY] || !isJoystick3dTarget(obj)) return null;
  const pos = obj.oCoords?.[KEY];
  const rect = canvas.upperCanvasEl?.getBoundingClientRect?.();
  if (!pos || !rect) return null;
  const dx = e.clientX - rect.left - pos.x;
  const dy = e.clientY - rect.top - pos.y;
  return dx * dx + dy * dy <= (PAD_R + 5) * (PAD_R + 5) ? obj : null;
}

function resetTilt(canvas, obj) {
  const ctx = CTX.get(canvas);
  if (!ctx) return;
  if (kindOf(obj) === 'fx') {
    ctx.patchTextFx('extrude', { ...DEFAULT_TILT });
    fxRequestUpgrade(obj, { scaleMul: 1 });
    ctx.commit(); // ONE entry, same as a release
    canvas.requestRenderAll();
  } else {
    ctx.regenerateText3d(obj, { rotation: { ...DEFAULT_TILT } })
      .catch(() => { /* engine logs */ });
  }
}

function onDblClickCapture(e) {
  for (const canvas of CANVASES) {
    const obj = padHit(canvas, e);
    if (!obj) continue;
    // Swallow BEFORE fabric's element listener sees it: a dblclick on the
    // pad must reset the tilt — not enter text editing (fx Textbox) and not
    // open the 3D edit modal (text3d image).
    e.preventDefault();
    e.stopPropagation();
    resetTilt(canvas, obj);
    return;
  }
}

// ------------------------------------------------------------------- wiring
/**
 * Hand the engine facade slice to this canvas's pads and arm the dblclick
 * guard. Called by useCanvasEditor whenever the facade rebuilds (identities
 * refresh — the WeakMap entry is just replaced).
 * @param {object} canvas fabric canvas
 * @param {{patchTextFx:Function, regenerateText3d:Function, commit:Function}} ctx
 */
export function setJoystick3dContext(canvas, ctx) {
  if (!canvas || !ctx) return;
  const arm = CANVASES.size === 0;
  CTX.set(canvas, ctx);
  CANVASES.add(canvas);
  if (arm && typeof window !== 'undefined') {
    window.addEventListener('dblclick', onDblClickCapture, true);
  }
}

/** Drop the canvas's facade + disarm the window guard with the last canvas.
 * Mid-drag teardown (canvas dispose) just abandons the drag state. */
export function teardownJoystick3d(canvas) {
  if (!canvas) return;
  CTX.delete(canvas);
  CANVASES.delete(canvas);
  if (drag && drag.obj?.canvas === canvas) drag = null;
  if (CANVASES.size === 0 && typeof window !== 'undefined') {
    window.removeEventListener('dblclick', onDblClickCapture, true);
  }
}
